import request from '@/config/axios'

/**
 * 基础数据模块接口层（basedata-service）
 *
 * 契约（见设计文档《基础数据模块技术方案》§4）：
 *   管理端前缀 /admin/basedata，权限码前缀 basedata.*
 *   管理端条目读 /api/basedata/admin/{typeCode}/items（分页）与 /items/all（全量）；
 *   公开只读 /api/basedata/dict/{typeCode}/items 供消费端使用（仅启用项，读发布快照）。
 *
 * 本项目请求层 base_url 为空、dev server 只代理 /api 前缀，因此真实请求里
 * 把契约的「/admin/basedata」落到「/api/basedata/admin」——与其他服务
 * （/api/identity/admin、/api/channel/admin）保持一致；待后端网关路由确认后微调。
 */

// ==================== 类型 ====================

/** 敏感级别：低敏感 / 高敏感 */
export type SensitiveLevel = 'LOW' | 'HIGH'

/** 字典分类：系统字典（全模块通用）/ 业务字典（挂所属模块） */
export type DictCategory = 'SYSTEM' | 'BUSINESS'

/** 系统模块字典类型码（业务字典选所属模块的数据源） */
export const MODULE_TYPE_CODE = 'MODULE'

/** 入口类型字典类型码（权限点的 terminalId 数据源） */
export const ENTRY_CLASS_TYPE_CODE = 'system_entry_class'

/** 条目状态 */
export type DictItemStatus = 'ENABLED' | 'DISABLED'

/** 变更单状态 */
export type ChangeStatus = 'DRAFT' | 'PUBLISHED' | 'REJECTED'

/** 批量编辑操作类型（禁止物理删除，删除 = 停用；已停用条目可恢复启用） */
export type BatchOpType = 'CREATE' | 'UPDATE' | 'DISABLE' | 'ENABLE'

/** 字典类型（dict_type） */
export interface DictTypeVO {
  typeCode: string
  name: string
  /** 字典分类：系统字典（全模块通用）/ 业务字典（挂所属模块） */
  category: DictCategory
  /** 所属模块码（仅业务字典有值） */
  moduleCode?: string
  sensitiveLevel: SensitiveLevel
  /** 直改生效开关：开启后该类型免复核直接发布 */
  directEdit: boolean
  /** 是否树形结构（行政区划为树形，其余平面） */
  treeType: boolean
}

/** 字典条目（dict_item / 独立表统一视图） */
export interface DictItemVO {
  code: string
  name: string
  parentCode?: string
  level: number
  sortOrder: number
  status: DictItemStatus
  children?: DictItemVO[]
  remark?: string
  /** 业务字段（独立表类型专属列，如区域区号/银行机构码；通用表类型无此键） */
  extra?: Record<string, unknown>
}

/** 业务字段元信息（GET {typeCode}/fields 驱动动态渲染） */
export interface TypeFieldMeta {
  key: string
  label: string
  /** TEXT / SELECT */
  input: 'TEXT' | 'SELECT'
  options?: Array<{ value: string; label: string }>
  hidden?: boolean
  required?: boolean
}

/** 新增字典类型入参（POST types） */
export interface CreateTypeParams {
  typeCode: string
  name: string
  /** 字典分类：系统字典（全模块通用）/ 业务字典（挂所属模块） */
  category: DictCategory
  /** 所属模块码（业务字典必填，引用 MODULE 字典条目） */
  moduleCode?: string
  sensitiveLevel: SensitiveLevel
  /** 结构：true=树形（有父子层级），false=平面 */
  treeType: boolean
  /** 直改生效：高敏感类型不可开启 */
  directEdit: boolean
  remark?: string
}

/** 批量编辑入参（POST items/batch） */
export interface BatchItemOp {
  op: BatchOpType
  code: string
  name?: string
  parentCode?: string
  sortOrder?: number
  /** 创建即停用（仅 CREATE 生效）：新增后默认停用，确认后手动启用 */
  disabled?: boolean
  /** 业务字段目标值（仅 CREATE/UPDATE 生效） */
  extra?: Record<string, unknown>
}

/** REGION 轻量写操作（单行写，不生成全量版本快照） */
export type RegionWriteOpType = 'CREATE' | 'UPDATE' | 'DISABLE' | 'ENABLE'

export interface RegionWriteOp {
  op: RegionWriteOpType
  code: string
  name?: string
  parentCode?: string
  /** PROVINCE/CITY/DISTRICT/TOWN/COMMUNITY/BUSINESS */
  regionLevel?: string
  /** OPEN/PAUSED/CLOSED */
  openStatus?: string
  sortOrder?: number
}

/** 变更单逐条 op（后端 DictChangeItem 平铺目标值，无 after 字段） */
export interface ChangeOpDetail {
  op: BatchOpType
  code: string
  /** 目标值（CREATE/UPDATE 生效） */
  name?: string
  parentCode?: string
  parentChanged?: boolean
  sortOrder?: number
  disabled?: boolean
  extra?: Record<string, unknown>
  /** 旧值快照（beforeOf：code/name/parentCode/level/sortOrder/status，业务字段非空时含 extra） */
  before?: {
    code?: string
    name?: string
    parentCode?: string
    level?: number
    sortOrder?: number
    status?: number
    extra?: Record<string, unknown>
  }
}

/** 后端变更单 JSON（changeDetail/batchEdit/review 返回的 Map；列表接口返回 DictChange 实体同构但无 items） */
export interface DictChangeRaw {
  id: number
  changeNo: string
  dictType: string
  title: string
  status: ChangeStatus
  source: string
  operatorId?: number
  operatorName?: string
  reviewerId?: number
  reviewerName?: string
  reviewedAt?: string
  createTime?: string
  items?: ChangeOpDetail[]
  changesJson?: string
}

/** 后端 DictChange JSON → 展示 VO（列表/详情共用映射；列表无 items 时解析 changesJson 统计操作数） */
export const toChangeVO = (raw: DictChangeRaw, types: DictTypeVO[]): ChangeOrderVO => {
  let ops = raw.items ?? []
  if (!ops.length && raw.changesJson) {
    try {
      ops = JSON.parse(raw.changesJson) as ChangeOpDetail[]
    } catch {
      ops = []
    }
  }
  return {
    id: raw.id,
    typeCode: raw.dictType,
    typeName: types.find((t) => t.typeCode === raw.dictType)?.name ?? raw.dictType,
    status: raw.status,
    opCount: ops.length,
    createdBy: raw.operatorName ?? '—',
    createdAt: raw.createTime ?? '',
    reviewedBy: raw.reviewerName,
    reviewedAt: raw.reviewedAt,
    ops
  }
}

/** 变更单展示 VO（由 toChangeVO 从后端 JSON 映射） */
export interface ChangeOrderVO {
  id: number
  typeCode: string
  typeName: string
  status: ChangeStatus
  opCount: number
  createdBy: string
  createdAt: string
  reviewedBy?: string
  reviewedAt?: string
  ops: ChangeOpDetail[]
}

/** 版本快照条目（snapshot JSON 数组元素） */
export interface VersionSnapshotItem {
  code: string
  name: string
  parentCode?: string
  level?: number
  sortOrder?: number
  status?: number
  extra?: Record<string, unknown>
}

/** 版本（dict_version，接口直接返回实体，含全量快照） */
export interface VersionVO {
  id: number
  dictType: string
  versionNo: number
  /** 该版本全量条目快照（JSON 字符串，解析为 VersionSnapshotItem[]） */
  snapshot?: string
  publisherId?: number
  publisherName?: string
  publishedAt?: string
}

/** 回滚结果（直改生效类型：立即重新发布） */
export interface RollbackResult {
  dictType: string
  version: number
  rolledBackFrom: number
}

/** Excel 导入校验结果 */
export interface ImportCheckResult {
  total: number
  valid: DictItemVO[]
  errors: Array<{ row: number; reason: string }>
}

export interface PageResult<T> {
  list: T[]
  total: number
}

export interface PageParam {
  pageNo?: number
  pageSize?: number
}

/** 行政区节点（懒加载树，GET /dict/region/nodes） */
export interface RegionDictNodeVO {
  id: number
  code: string
  name: string
  parentCode?: string | null
  /** PROVINCE / CITY / DISTRICT / TOWN / COMMUNITY / BUSINESS */
  regionLevel?: string
  /** 招商开放状态（仅 ADMIN） */
  openStatus?: string
  /** 层级深度 */
  depth?: number
  sortOrder?: number
  /** 是否有子节点，树上用来控制懒加载展开 */
  hasChildren?: boolean
}

/** 业务区已认领的行政区 */
export interface BizAreaRelationVO {
  relationId: number
  bizAreaId: number
  regionId: number
  regionCode: string
  regionName: string
  regionLevel?: string
  parentCode?: string
  sortOrder: number
  status?: number
  regionPath?: string
}

/** 待绑定的候选行政区 */
export interface BizAreaCandidateVO {
  regionId: number
  regionCode: string
  regionName: string
  regionLevel?: string
  regionPath?: string
}

/** 批量认领结果 */
export interface BizAreaClaimResult {
  claimed: number
  skipped: number
}

// ==================== 接口 ====================
export const BasedataApi = {
  /** 字典类型列表 @returns 字典类型数组 */
  getTypes: async (): Promise<DictTypeVO[]> => {
    return await request.get({ url: '/api/basedata/admin/types' })
  },

  /**
   * 新增字典类型
   * @param data 类型参数
   * @returns 新建的字典类型
   */
  createType: async (data: CreateTypeParams): Promise<DictTypeVO> => {
    return await request.post({ url: '/api/basedata/admin/types', data })
  },

  /**
   * 更新字典类型（当前用于「直改生效」开关）
   * @param typeCode 类型编码
   * @param data 更新字段
   */
  updateType: async (typeCode: string, data: Partial<DictTypeVO>): Promise<void> => {
    return await request.put({ url: `/api/basedata/admin/types/${typeCode}`, data })
  },

  /** 类型业务字段元信息（驱动动态渲染） @param typeCode 类型编码 */
  getTypeFields: async (typeCode: string): Promise<TypeFieldMeta[]> => {
    return await request.get({ url: `/api/basedata/admin/${typeCode}/fields` })
  },

  /**
   * 条目分页查询
   * @param typeCode 类型编码
   * @param params 分页与过滤参数
   */
  getItems: async (
    typeCode: string,
    params: {
      parentCode?: string
      keyword?: string
      status?: DictItemStatus
      /** 最大层级深度：1=只到第一级，2=前两级，逐级递增；不传=不限 */
      maxLevel?: number
    } & PageParam
  ): Promise<PageResult<DictItemVO>> => {
    return await request.get({ url: `/api/basedata/admin/${typeCode}/items`, params })
  },

  /**
   * 条目全量查询（不分页，含停用项；管理端树形类型加载用）
   * @param typeCode 类型编码
   * @param params 过滤参数；maxLevel 按 1=第一级截断，不传=不限
   */
  getAllItems: async (
    typeCode: string,
    params: {
      parentCode?: string
      keyword?: string
      status?: DictItemStatus
      maxLevel?: number
    } = {}
  ): Promise<DictItemVO[]> => {
    return await request.get({ url: `/api/basedata/admin/${typeCode}/items/all`, params })
  },

  /**
   * 条目一级子节点（管理端懒加载树用，一次只返回直接下级）
   * @param typeCode 类型编码
   * @param params parentCode 为空=根节点
   */
  getItemChildren: async (
    typeCode: string,
    params: {
      parentCode?: string
      keyword?: string
      status?: DictItemStatus
    } = {}
  ): Promise<DictItemVO[]> => {
    return await request.get({ url: `/api/basedata/admin/${typeCode}/items/children`, params })
  },

  /**
   * 树形条目根分页：只对根节点分页，返回当前页根节点及其全部子孙（扁平，前端按 parentCode 重组树）
   * @param typeCode 类型编码
   * @param params 根节点分页参数
   */
  getItemTreePage: async (
    typeCode: string,
    params: PageParam = {}
  ): Promise<PageResult<DictItemVO>> => {
    return await request.get({ url: `/api/basedata/admin/${typeCode}/items/tree-page`, params })
  },

  /**
   * 公开只读条目（仅启用项，树形父码返回整个子树）
   * @param typeCode 类型编码
   * @param params parentCode 子树根；maxLevel 按 1=第一级截断
   */
  getPublicItems: async (
    typeCode: string,
    params: { parentCode?: string; maxLevel?: number } = {}
  ): Promise<DictItemVO[]> => {
    return await request.get({ url: `/api/basedata/dict/${typeCode}/items`, params })
  },

  /**
   * 行政区节点（按层懒加载：不传 parentCode 返回根节点，展开时传当前节点 code）
   * @param parentCode 上级区划码
   */
  getRegionNodes: async (parentCode?: string): Promise<RegionDictNodeVO[]> => {
    return await request.get({
      url: '/api/basedata/dict/region/nodes',
      params: parentCode ? { parentCode } : undefined
    })
  },

  /**
   * 批量编辑条目 → 变更单 DRAFT（直改类型已发布）
   * @param typeCode 类型编码
   * @param ops 批量操作
   */
  submitBatch: async (typeCode: string, ops: BatchItemOp[]): Promise<DictChangeRaw> => {
    return await request.post({ url: `/api/basedata/admin/${typeCode}/items/batch`, data: ops })
  },

  /**
   * REGION 轻量写（新增/改名/启停/开放状态）
   * 单行写 + Redis hash 增量，不生成全量版本快照。
   */
  submitRegionOps: async (ops: RegionWriteOp[]): Promise<void> => {
    return await request.post({ url: '/api/basedata/admin/regions/ops', data: ops })
  },

  /** 变更单列表（后端返回 DictChange 实体，经 toChangeVO 映射） @param params 状态/类型过滤与分页 */
  getChanges: async (
    params: { status?: ChangeStatus; typeCode?: string } & PageParam
  ): Promise<PageResult<DictChangeRaw>> => {
    return await request.get({ url: '/api/basedata/admin/changes', params })
  },

  /** 变更单详情 @param id 变更单 ID */
  getChange: async (id: number): Promise<DictChangeRaw> => {
    return await request.get({ url: `/api/basedata/admin/changes/${id}` })
  },
  /** 复核通过 → 发布 */
  reviewChange: async (id: number): Promise<void> => {
    return await request.post({ url: `/api/basedata/admin/changes/${id}/review` })
  },

  /** 驳回变更单 */
  rejectChange: async (id: number): Promise<void> => {
    return await request.post({ url: `/api/basedata/admin/changes/${id}/reject` })
  },

  /** 版本列表 */
  getVersions: async (typeCode: string): Promise<VersionVO[]> => {
    return await request.get({ url: `/api/basedata/admin/${typeCode}/versions` })
  },

  /**
   * 回滚版本。直改生效类型立即发布（RollbackResult）；高敏感/需复核类型返回回滚变更单（DRAFT，复核后发布）
   * @param typeCode 类型编码
   * @param versionNo 版本号
   */
  rollbackVersion: async (
    typeCode: string,
    versionNo: number
  ): Promise<RollbackResult | DictChangeRaw> => {
    return await request.post({
      url: `/api/basedata/admin/${typeCode}/versions/${versionNo}/rollback`
    })
  },

  /** Excel 导入（校验） @param typeCode 类型编码 @param file 上传文件 */
  checkImport: async (typeCode: string, file: File): Promise<ImportCheckResult> => {
    const form = new FormData()
    form.append('file', file)
    return await request.upload({ url: `/api/basedata/admin/${typeCode}/import/check`, data: form })
  },

  /** 确认导入生成变更单（返回变更单原始实体） @param typeCode 类型编码 @param items 校验通过的条目 */
  confirmImport: async (typeCode: string, items: DictItemVO[]): Promise<DictChangeRaw> => {
    return await request.post({
      url: `/api/basedata/admin/${typeCode}/import/confirm`,
      data: items
    })
  },

  /** 业务区已认领行政区（分页） */
  getBizAreaRelations: async (
    bizAreaCode: string,
    params: PageParam
  ): Promise<PageResult<BizAreaRelationVO>> => {
    return await request.get({
      url: `/api/basedata/admin/biz-areas/${encodeURIComponent(bizAreaCode)}/relations`,
      params
    })
  },

  /** 业务区待绑定候选行政区（parentCode 空=根/省；非空=直接下级） */
  getBizAreaCandidates: async (
    bizAreaCode: string,
    params: { parentCode?: string; keyword?: string } & PageParam
  ): Promise<PageResult<BizAreaCandidateVO>> => {
    return await request.get({
      url: `/api/basedata/admin/biz-areas/${encodeURIComponent(bizAreaCode)}/relation-candidates`,
      params
    })
  },

  /** 批量认领行政区 */
  claimBizAreaRegions: async (
    bizAreaCode: string,
    regionIds: number[]
  ): Promise<BizAreaClaimResult> => {
    return await request.post({
      url: `/api/basedata/admin/biz-areas/${encodeURIComponent(bizAreaCode)}/relations`,
      data: { regionIds }
    })
  },

  /** 退领单个行政区 */
  unclaimBizAreaRegion: async (bizAreaCode: string, regionId: number): Promise<void> => {
    return await request.delete({
      url: `/api/basedata/admin/biz-areas/${encodeURIComponent(bizAreaCode)}/relations/${regionId}`
    })
  }
}
